import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Button,
    DialogProps,
    Typography,
    TextField,
    FormHelperText,
    Autocomplete,
    ListItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, {useEffect, useState} from 'react';
import {INomenclatureGroup} from '../../interfaces/fetch/Nomenclature';
import {NomenclatureGroupEditPaper} from '../../ui/NomenclatureGroupEditPaper';

type NomenclatureMoveToGroupPopupProps = {
    open: boolean;
    userId: number | null;
    onClose: () => void;
    selectedRows: Set<string>;
    handleMoveNomenclatureListToGroupSelected: (nomenclatureGroupId: number | null) => Promise<void>;
    nomenclatureGroups?: INomenclatureGroup[];
    onManageGroups: () => void;
} & Pick<DialogProps, 'fullWidth' | 'maxWidth'>;

export const NomenclatureMoveToGroupPopup: React.FC<NomenclatureMoveToGroupPopupProps> = ({
    open,
    userId,
    onClose,
    fullWidth = true,
    maxWidth = 'sm',
    handleMoveNomenclatureListToGroupSelected,
    nomenclatureGroups = [],
    onManageGroups,
}) => {
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);

    useEffect(() => {
        if (!open) {
            setSelectedGroupId(null);
            return;
        }
        if (!userId) return;
    }, [open, userId]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth={fullWidth} maxWidth={maxWidth}>
            <Box sx={{p: '20px 24px 10px 3px', position: 'relative', backgroundColor: '#f5f7ff'}}>
                <DialogTitle sx={{pr: 5}}>Перемещение</DialogTitle>
                <IconButton onClick={onClose} sx={{position: 'absolute', right: 16, top: 16}}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} pb={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Выберите группу номенклатуры, в&nbsp;которую хотите переместить выделенные позиции
                    </Typography>

                    <Autocomplete
                        options={nomenclatureGroups}
                        PaperComponent={props => <NomenclatureGroupEditPaper {...props} onManageGroups={onManageGroups} />}
                        getOptionLabel={option => option.name}
                        value={nomenclatureGroups.find(g => g.id === selectedGroupId) || null}
                        onChange={(event, newValue) => (newValue ? setSelectedGroupId(newValue.id) : setSelectedGroupId(null))}
                        renderOption={(props, option) => (
                            <ListItem {...props}>
                                <Typography sx={{paddingRight: '35px', width: '100%'}}>{option.name}</Typography>
                            </ListItem>
                        )}
                        renderInput={params => (
                            <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                placeholder="Группа номенклатуры"
                                sx={{
                                    minWidth: 250,
                                    backgroundColor: '#fff',
                                    '.MuiOutlinedInput-root': {
                                        height: '44px',
                                        paddingTop: '0',
                                        paddingBottom: '0',
                                    },
                                }}
                            />
                        )}
                        disableClearable={selectedGroupId === null}
                    />
                    <FormHelperText>
                        Если хотите убрать выделенные позиции из&nbsp;группы оставьте поле пустым и&nbsp;нажмите &laquo;Переместить&raquo;
                    </FormHelperText>
                </Box>
                <Box sx={{mt: 2, mb: 1}}>
                    <Button
                        variant="contained"
                        onClick={() => {
                            void onClose();
                            void handleMoveNomenclatureListToGroupSelected(selectedGroupId);
                        }}
                        sx={{mr: 2}}
                    >
                        Переместить
                    </Button>
                    <Button variant="text" onClick={onClose}>
                        Отмена
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
